import { FIRST_PAGE_INDEX } from 'constants/common';
import {
  CONNECT_ABUSING_COMMUNITY,
  CONNECT_ABUSING_PAYMENT,
  INITIAL_CONNECT_MEMBER_FILTER_VALUES,
} from 'constants/member/connect';
import { connectMemberListActions } from 'features/member/reducer/connectMemberListSlice';
import keyboardKey from 'keyboard-key';
import { getEnumValues } from 'modules/utils/enum';
import { addQueryString, getNewValueForQuery, getParsedQuery } from 'modules/utils/queryString';
import { ChangeEvent, useEffect, useState, KeyboardEvent } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CheckObjectValue } from 'types/common';
import {
  ConnectUserType,
  ConnectCommunityUserType,
  ConnectCommunityUserStatus,
  ConnectUserStatus,
  ConnectAchievementBadge,
  GetConnectMemberListParams,
} from 'types/member';

// 선택된 값이 없으면(default) 필터 미적용 상태로 전체 값 조회
const getCheckedValuesFromQuery = (
  queryValues: string[] | undefined,
  values: number[],
): CheckObjectValue => {
  return values.reduce((acc, value) => {
    acc[value] = (queryValues ?? []).includes(String(value));
    return acc;
  }, {} as CheckObjectValue);
};

const useConnectMemberSearchFilter = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [keyword, setKeyword] = useState<string>('');
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(FIRST_PAGE_INDEX);
  const [connectAbusingUserStatus, setConnectAbusingUserStatus] = useState<CheckObjectValue>(
    INITIAL_CONNECT_MEMBER_FILTER_VALUES.connectAbusingUserStatus,
  );
  const [connectUserType, setConnectUserType] = useState<CheckObjectValue>(
    INITIAL_CONNECT_MEMBER_FILTER_VALUES.connectUserType,
  );
  const [connectCommunityUserType, setConnectCommunityUserType] = useState<CheckObjectValue>(
    INITIAL_CONNECT_MEMBER_FILTER_VALUES.connectCommunityUserType,
  );
  const [connectAchievementBadge, setConnectAchievementBadge] = useState<CheckObjectValue>(
    INITIAL_CONNECT_MEMBER_FILTER_VALUES.connectAchievementBadge,
  );

  // 선택된 값만 배열로 변환. 빈 배열이면 필터 미적용(전체 검색)
  const getSelectedValues = (checkObject: CheckObjectValue): number[] =>
    Object.entries(checkObject)
      .filter(([_, isChecked]) => isChecked)
      .map(([value, _]) => Number(value));

  // abusing user 선택 -> 해당 서비스 status INACTIVE 조건으로 조회 (미선택시 파라미터 생략)
  const getConnectMemberListParams = ({
    keyword,
    page,
    abusingUserStatus,
    userType,
    communityUserType,
    achievementBadge,
  }: {
    keyword: string;
    page: number;
    abusingUserStatus: CheckObjectValue;
    userType: CheckObjectValue;
    communityUserType: CheckObjectValue;
    achievementBadge: CheckObjectValue;
  }): GetConnectMemberListParams => ({
    keyword,
    page,
    connectUserType: getSelectedValues(userType),
    connectCommunityUserType: getSelectedValues(communityUserType),
    connectAchievementBadge: getSelectedValues(achievementBadge),
    connectUserStatus: abusingUserStatus[CONNECT_ABUSING_PAYMENT]
      ? ConnectUserStatus.INACTIVE
      : undefined,
    connectCommunityUserStatus: abusingUserStatus[CONNECT_ABUSING_COMMUNITY]
      ? ConnectCommunityUserStatus.INACTIVE
      : undefined,
  });

  const onChangeConnectAbusingUser = (values: string[]): void => {
    setConnectAbusingUserStatus({
      [CONNECT_ABUSING_PAYMENT]: values.includes(CONNECT_ABUSING_PAYMENT),
      [CONNECT_ABUSING_COMMUNITY]: values.includes(CONNECT_ABUSING_COMMUNITY),
    });
  };

  const getCheckedValues = (allValues: number[], selectedValues: number[]): CheckObjectValue => {
    return allValues.reduce((acc, value) => {
      acc[value] = selectedValues.includes(value);
      return acc;
    }, {} as CheckObjectValue);
  };

  const onChangeConnectUserType = (values: ConnectUserType[]): void => {
    setConnectUserType(getCheckedValues(getEnumValues(ConnectUserType), values));
  };

  const onChangeConnectCommunityUserType = (values: ConnectCommunityUserType[]): void => {
    setConnectCommunityUserType(getCheckedValues(getEnumValues(ConnectCommunityUserType), values));
  };

  const onChangeConnectAchievementBadge = (values: ConnectAchievementBadge[]): void => {
    setConnectAchievementBadge(getCheckedValues(getEnumValues(ConnectAchievementBadge), values));
  };

  const onChangeKeyword = (e: ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
  };

  const onChangePage = (pageIndex: number) => {
    addQueryString({ history, objValue: { page: pageIndex } });
    setCurrentPageIndex(pageIndex);
    dispatch(
      connectMemberListActions.getConnectMemberList(
        getConnectMemberListParams({
          keyword,
          page: pageIndex,
          abusingUserStatus: connectAbusingUserStatus,
          userType: connectUserType,
          communityUserType: connectCommunityUserType,
          achievementBadge: connectAchievementBadge,
        }),
      ),
    );
  };

  const getSearchResult = () => {
    addQueryString({
      history,
      objValue: {
        keyword,
        connectUserType: getNewValueForQuery(connectUserType),
        connectCommunityUserType: getNewValueForQuery(connectCommunityUserType),
        connectAchievementBadge: getNewValueForQuery(connectAchievementBadge),
        connectAbusingUser: getNewValueForQuery(connectAbusingUserStatus),
        page: FIRST_PAGE_INDEX,
      },
    });
  };

  const onKeyDownKeyword = (e: KeyboardEvent<HTMLInputElement>) => {
    const inputKey = keyboardKey.getCode(e);
    if (inputKey === keyboardKey.Enter) {
      getSearchResult();
    }
  };

  const resetFilter = () => {
    addQueryString({ history, reset: true });
    setKeyword('');
    setCurrentPageIndex(FIRST_PAGE_INDEX);
    setConnectAbusingUserStatus(INITIAL_CONNECT_MEMBER_FILTER_VALUES.connectAbusingUserStatus);
    setConnectUserType(INITIAL_CONNECT_MEMBER_FILTER_VALUES.connectUserType);
    setConnectCommunityUserType(INITIAL_CONNECT_MEMBER_FILTER_VALUES.connectCommunityUserType);
    setConnectAchievementBadge(INITIAL_CONNECT_MEMBER_FILTER_VALUES.connectAchievementBadge);

    dispatch(
      connectMemberListActions.getConnectMemberList(
        getConnectMemberListParams({
          keyword: '',
          page: FIRST_PAGE_INDEX,
          abusingUserStatus: INITIAL_CONNECT_MEMBER_FILTER_VALUES.connectAbusingUserStatus,
          userType: INITIAL_CONNECT_MEMBER_FILTER_VALUES.connectUserType,
          communityUserType: INITIAL_CONNECT_MEMBER_FILTER_VALUES.connectCommunityUserType,
          achievementBadge: INITIAL_CONNECT_MEMBER_FILTER_VALUES.connectAchievementBadge,
        }),
      ),
    );
  };

  useEffect(() => {
    // 새로고침시 url query 읽어와서 세팅
    let keyword = '';
    let page = FIRST_PAGE_INDEX;
    let {
      connectAbusingUserStatus,
      connectUserType,
      connectCommunityUserType,
      connectAchievementBadge,
    } = INITIAL_CONNECT_MEMBER_FILTER_VALUES;

    const queries = history.location.search;

    if (queries.length) {
      const parsedQuery = getParsedQuery(queries);
      keyword = parsedQuery['keyword']?.[0] ?? '';
      page = +(parsedQuery['page']?.[0] ?? FIRST_PAGE_INDEX);

      const abusingUserFromQuery = parsedQuery['connectAbusingUser'] ?? [];
      connectAbusingUserStatus = {
        [CONNECT_ABUSING_PAYMENT]: abusingUserFromQuery.includes(CONNECT_ABUSING_PAYMENT),
        [CONNECT_ABUSING_COMMUNITY]: abusingUserFromQuery.includes(CONNECT_ABUSING_COMMUNITY),
      };

      connectUserType = getCheckedValuesFromQuery(
        parsedQuery['connectUserType'],
        getEnumValues(ConnectUserType),
      );
      connectCommunityUserType = getCheckedValuesFromQuery(
        parsedQuery['connectCommunityUserType'],
        getEnumValues(ConnectCommunityUserType),
      );
      connectAchievementBadge = getCheckedValuesFromQuery(
        parsedQuery['connectAchievementBadge'],
        getEnumValues(ConnectAchievementBadge),
      );
    }

    setKeyword(keyword);
    setCurrentPageIndex(page);
    setConnectAbusingUserStatus(connectAbusingUserStatus);
    setConnectUserType(connectUserType);
    setConnectCommunityUserType(connectCommunityUserType);
    setConnectAchievementBadge(connectAchievementBadge);
    dispatch(
      connectMemberListActions.getConnectMemberList(
        getConnectMemberListParams({
          keyword,
          page,
          abusingUserStatus: connectAbusingUserStatus,
          userType: connectUserType,
          communityUserType: connectCommunityUserType,
          achievementBadge: connectAchievementBadge,
        }),
      ),
    );
  }, [history.location.search]);

  return {
    currentPageIndex,
    keyword,
    connectAbusingUserStatus,
    connectUserType,
    connectCommunityUserType,
    connectAchievementBadge,
    onChangeConnectAbusingUser,
    onChangeConnectUserType,
    onChangeConnectCommunityUserType,
    onChangeConnectAchievementBadge,
    onChangeKeyword,
    onKeyDownKeyword,
    getSearchResult,
    onChangePage,
    resetFilter,
  };
};

export default useConnectMemberSearchFilter;
